import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Mic2, Trash2, Upload } from "lucide-react";
import { deleteTake, fmtClock, shortVoiceName, uploadTakeFile } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const ACCEPT = "audio/*,.wav,.mp3,.m4a,.aac,.flac,.aif,.aiff,.ogg";

function TakeCard({ take, selected, onPick, onDelete, disabled }) {
  const uploaded = take.source === "upload";
  return (
    <div className="relative">
      <button type="button" role="radio" aria-checked={selected} data-testid={`replace-take-${take.id}`} disabled={disabled} onClick={() => onPick(take.id)}
        className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 pr-11 text-left transition-colors duration-200 disabled:opacity-50 ${selected ? "border-amber-300/60 bg-amber-500/[0.12]" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.07]"}`}>
        <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${selected ? "bg-amber-300 text-[#05070f]" : "bg-white/[0.08] text-white/60"}`}>
          {selected ? <Check className="h-4 w-4" /> : uploaded ? <Upload className="h-4 w-4" /> : <Mic2 className="h-4 w-4" />}
        </span>
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2 font-display text-[14px] font-semibold">
            Take {take.take_number || 1}
            <span className={`rounded-full border px-1.5 py-px font-mono text-[9px] uppercase tracking-wider ${uploaded ? "border-sky-300/40 text-sky-200" : "border-rose-300/40 text-rose-200"}`}>{uploaded ? "Uploaded" : "Recorded here"}</span>
          </span>
          <span className="mt-0.5 block truncate text-[12px] text-white/55">
            {shortVoiceName(take.singer_name) || "Me"}{take.duration ? ` · ${fmtClock(take.duration)}` : ""} · {new Date(take.created_at).toLocaleDateString()}
          </span>
          {uploaded && take.filename ? <span className="block truncate font-mono text-[10px] text-white/35">{take.filename}</span> : null}
        </span>
      </button>
      <button type="button" data-testid={`replace-take-delete-${take.id}`} onClick={() => onDelete(take)} disabled={disabled} aria-label={`Remove take ${take.take_number || 1}`}
        className="absolute right-2.5 top-2.5 grid h-7 w-7 place-items-center rounded-full text-white/35 transition-colors hover:bg-white/[0.08] hover:text-rose-300 disabled:opacity-40">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Step 1 — one of YOUR takes on this version: recorded in "Sing it myself", or a vocal file from another studio. */
export default function TakePicker({ projectId, versionId, takes, takeId, onPick, onProject, disabled }) {
  const [uploading, setUploading] = useState(false);
  const input = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const p = await uploadTakeFile(projectId, { file, versionId });
      onProject(p);
      const newest = (p.takes || []).filter((t) => t.version_id === versionId).pop();
      if (newest) onPick(newest.id);
      toast.success("Vocal uploaded", { description: "It's ready to drop into the song." });
    } catch (e) { toast.error("Couldn't upload that vocal", { description: safeApiError(e) }); }
    finally { setUploading(false); if (input.current) input.current.value = ""; }
  };
  const remove = async (take) => {
    if (!window.confirm(`Remove Take ${take.take_number || 1} from this list? Versions you already made with it stay in the project.`)) return;
    try { onProject(await deleteTake(projectId, take.id)); toast.success("Take removed"); }
    catch (e) { toast.error("Couldn't remove the take", { description: safeApiError(e) }); }
  };
  return (
    <div>
      {takes.length ? (
        <div role="radiogroup" data-testid="replace-take-list" className="grid gap-2 sm:grid-cols-2">
          {takes.map((t) => <TakeCard key={t.id} take={t} selected={t.id === takeId} onPick={onPick} onDelete={remove} disabled={disabled} />)}
        </div>
      ) : (
        <p data-testid="replace-no-takes" className="rounded-2xl border border-dashed border-white/10 p-5 text-[13px] text-white/55">
          No takes on this version yet — record one with your microphone, or upload a vocal you recorded somewhere else.
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/create/project/${projectId}/sing?version=${versionId}`} data-testid="replace-record-new"
          className="inline-flex items-center gap-2 rounded-full border border-rose-300/40 bg-rose-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-rose-100 hover:bg-rose-500/20">
          <Mic2 className="h-3.5 w-3.5" /> Record a new take
        </Link>
        <button type="button" data-testid="replace-upload-btn" onClick={() => input.current?.click()} disabled={uploading || disabled}
          className="inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-500/10 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-sky-100 hover:bg-sky-500/20 disabled:opacity-40">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {uploading ? "Uploading…" : "Upload a vocal file"}
        </button>
        <input ref={input} type="file" accept={ACCEPT} hidden data-testid="replace-upload-input" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
      <p className="mt-2 text-[11px] text-white/40">Uploaded vocals should start at 0:00 of this version (export the whole song length) — WAV, MP3, M4A, FLAC or AIFF.</p>
    </div>
  );
}
